import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  initialKeywords,
  MEDIA_TYPE,
  searchEngines,
} from "../../../../constants/segmentTools";
import {
  usePostMediaBrowserDataV2,
  useSaveExternalImage,
} from "../../../../services/mutations";
import EmptySearchContainer from "../EmptySearchContainer";
import ErrorSearchContainer from "../ErrorSearchContainer";
import InitialView from "../InitialView";
import MediaMasonry from "./MediaMasonry";
import * as Styled from "./styles";
import { saveRecentMedia } from "../../../../services/api";
import { useSelector } from "react-redux";
import { getWheelId } from "../../../../redux/selectors/segmentBuilderSelectors";
import Header from "./Header";
import { getRandomId } from "../../../../utils/helpers";
import {
  isExternalImageType,
  getRecentMediaFileType,
  getRecentMediaMetadataType,
} from "./mediaBrowserSelect";
import { shouldFetchNextPage } from "./scrollPagination";
import {
  normalizeBrowserMediaList,
  shouldShowInitialView,
} from "./mediaBrowserNormalize";

const MediaBrowser = ({
  mediaType = "image",
  onConfirm = () => {},
  isGifAllowed,
}) => {
  const {
    mutate,
    isLoading,
    data,
    isError: isRequestError,
  } = usePostMediaBrowserDataV2();
  const saveExternalImageMutation = useSaveExternalImage();
  const [selectedEngine, setSelectedEngine] = useState(
    searchEngines[mediaType][0].key
  );
  const [selectedMedia, setSelectedMedia] = useState([]);
  const [mediaKeyword, setMediaKeyword] = useState("");
  const [isNotFound, setIsNotFound] = useState(false);
  // gordon-0915-video-block-media-pane-pixabay-embed-first-record-tier item 1:
  // a real provider failure (success:false in the response body — see
  // get_media.py's error branch) is DISTINCT from a genuine zero-result
  // search (success:true, data:[]). Rendering both as EmptySearchContainer
  // masked real errors as "no results found".
  const [isError, setIsError] = useState(false);
  const [page, setPage] = useState(1);
  const [loadingMediaItems, setLoadingMediaItems] = useState(new Set());
  const containerRef = useRef(null);

  const SEARCH_LENGTH_LIMIT = 2;
  const wheelId = useSelector(getWheelId);

  const isGif =
    mediaType !== "video" &&
    isGifAllowed &&
    selectedEngine === searchEngines[mediaType][1]?.key;

  // Images/Documents: the second engine is Gifs, gated behind the
  // isGifAllowed flag/tier — dropdown only shows when that's actually on.
  // Video: the second engine is Pixabay (a real fallback source, not a
  // gated feature) — show the dropdown whenever there is a real second
  // engine to switch to, independent of the (image-only) isGifAllowed flag.
  const showDropdown =
    mediaType === "video"
      ? searchEngines[mediaType]?.length > 1
      : searchEngines[mediaType]?.length > 1 && isGifAllowed;

  const handleSearch = () => {
    // Trigger a search using current keyword and engine
    resetState();
    if (mediaKeyword.length > SEARCH_LENGTH_LIMIT) getMedia(mediaKeyword);
  };

  useEffect(() => {
    // A transport-level failure (BE unreachable, unhandled 5xx, CORS) never
    // reaches the body-shape check below — surface it as the same distinct
    // error state.
    if (isRequestError) {
      setIsError(true);
      setIsNotFound(false);
      return;
    }

    const body = data?.data;
    const rows = body?.data;
    if (rows) {
      // get_media.py returns success:false (with an empty data:[]) when the
      // provider call itself failed (bad/expired key, rate limit, malformed
      // response) — that must render as the error state, never the "no
      // results found" empty state a genuine zero-hit search gets.
      const providerFailed = body?.success === false;
      setIsError(!isLoading && providerFailed);
      setIsNotFound(!isLoading && !providerFailed && rows.length === 0);
      if (!providerFailed && rows.length > 0) {
        // QA-113 item 7: guard every field access — a Giphy row missing the
        // expected nested image variant must never throw and crash the builder.
        const normalized = normalizeBrowserMediaList(rows, isGif);
        if (normalized.length > 0) {
          setSelectedMedia((prevMedia) => [...prevMedia, ...normalized]);
        }
      }
    }
  }, [data, isGif, isLoading, isRequestError]);

  const getMedia = (
    text = "",
    source = selectedEngine,
    page = 1,
    per_page = 20
  ) => {
    let params = {
      text,
      source: isGif ? 1 : source,
      type: isGif ? MEDIA_TYPE.gif : MEDIA_TYPE[mediaType],
    };

    if (isGif) {
      // For GIFs, calculate offset and limit
      params.offset = (page - 1) * per_page; // Offset is (page - 1) * per_page
      params.limit = per_page; // Limit is per_page
    } else {
      // Default: use page and per_page for other media types
      params.page = page;
      params.per_page = per_page;
    }

    // Now mutate with the updated params (either with page/per_page or offset/limit)
    mutate(params);
  };

  const resetState = () => {
    setPage(1);
    setSelectedMedia([]);
    setIsNotFound(false);
    setIsError(false);
  };

  const handleRetry = () => {
    resetState();
    getMedia(mediaKeyword, selectedEngine, 1);
  };

  const handleMediaKeywordChange = (val) => {
    setMediaKeyword(val);
    // Do not search on type; search is triggered via search button or Enter key
    // Keep state ready; actual API call happens in handleSearch
    if (!val) {
      // If cleared, also clear stored media data and reset paging
      resetState();
    }
  };

  const handleInitialKeywordsClick = (val) => {
    setMediaKeyword(val);
    resetState();
    if (val.length > SEARCH_LENGTH_LIMIT) getMedia(val);
  };

  const handleEngineChange = (val) => {
    setSelectedEngine(val);
    resetState();
    setMediaKeyword("");
    // if (mediaKeyword.length > SEARCH_LENGTH_LIMIT) getMedia(mediaKeyword, val);
  };
  const handleSave = async (data) => {
    const mediaUrl = data.url || data.src;

    // Add media item to loading set
    setLoadingMediaItems((prev) => new Set([...prev, mediaUrl]));

    try {
      let finalData = { ...data };
      const id = getRandomId();

      // For images, first save the external image to get a persisted URL
      if (isExternalImageType(mediaType)) {
        onConfirm({ id, loading: true });
        const saveResponse = await saveExternalImageMutation.mutateAsync({
          url: mediaUrl,
          wheel_id: wheelId,
        });

        // Use the URL returned from the API
        finalData.src = saveResponse?.data?.data?.url;
      } else {
        // For non-images, use the original URL
        finalData.src = mediaUrl;
      }

      // Continue with the normal flow
      onConfirm({ ...finalData, id, loading: false });

      await saveRecentMedia(wheelId, {
        type: getRecentMediaFileType(mediaType),
        url: finalData.src || data.src,
        metadata: {
          type: getRecentMediaMetadataType(mediaType),
          thumbnail: data.thumbnail,
        },
      });
    } catch (error) {
      console.error("Error saving media:", error);
      // Still call onConfirm with original data if API call fails
      onConfirm({ ...data, loading: false });
    } finally {
      // Remove media item from loading set
      setLoadingMediaItems((prev) => {
        const newSet = new Set(prev);
        newSet.delete(mediaUrl);
        return newSet;
      });
    }
  };

  // QA-113 item 2: show the keyword-chip prompt for EVERY engine while there is
  // no active search term — Giphy was previously excluded here and fell through
  // to a blank grid ("no options to choose from").
  const shouldDisplayInitialView = shouldShowInitialView(
    mediaKeyword,
    SEARCH_LENGTH_LIMIT
  );

  const handleScroll = useCallback(() => {
    const container = containerRef.current;
    if (container) {
      const { scrollTop, scrollHeight, clientHeight } = container;
      if (
        shouldFetchNextPage(
          { scrollTop, clientHeight, scrollHeight },
          isLoading,
          isNotFound
        )
      ) {
        const nextPage = page + 1;
        setPage(nextPage);
        getMedia(mediaKeyword, selectedEngine, nextPage);
      }
    }
  }, [mediaKeyword, selectedEngine, page, isLoading, isNotFound]);

  useEffect(() => {
    const container = containerRef.current;
    if (container) {
      container.addEventListener("scroll", handleScroll);
      return () => container.removeEventListener("scroll", handleScroll);
    }
  }, [handleScroll]);

  const handleErrorMedia = (errorMedia) => {
    setSelectedMedia(selectedMedia.filter((media) => media !== errorMedia));
  };

  return (
    <Styled.Container width="100%">
      <Header
        selectedEngine={selectedEngine}
        handleEngineChange={handleEngineChange}
        handleChangeMediaKeyword={handleMediaKeywordChange}
        items={searchEngines[mediaType]}
        mediaKeyword={mediaKeyword}
        showDropdown={showDropdown}
        onSearch={handleSearch}
        mediaType={mediaType}
      />
      <Styled.MediaContainer ref={containerRef}>
        {shouldDisplayInitialView ? (
          <InitialView
            content={initialKeywords}
            handleSelectKeyword={handleInitialKeywordsClick}
          />
        ) : isError ? (
          <ErrorSearchContainer onRetry={handleRetry} />
        ) : isNotFound ? (
          <EmptySearchContainer />
        ) : (
          <MediaMasonry
            content={selectedMedia}
            mediaKeyword={mediaKeyword}
            isLoading={isLoading}
            onReplaceButtonPress={handleSave}
            handleErrorMedia={handleErrorMedia}
            mediaType={mediaType}
            loadingMediaItems={loadingMediaItems}
          />
        )}
      </Styled.MediaContainer>
    </Styled.Container>
  );
};

export default MediaBrowser;
